import { isNull, page, sql } from "@folio/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)               │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for the trash-enabled tables of this repo (`page`). Every
 * live read composes these predicates (`pages/queries.ts` builders,
 * ⌘K search, and any future read). The ONLY reads seeing deleted
 * rows are the trash view (`getTrashedPagesQuery`), structural
 * tree-walks in `pages/mutations.ts` (subtree cohort, needing to see
 * the ENTIRE tree including trash), and the id-scoped lookup `getPageQuery` (the route
 * decides what to display), all three declared in the guard whitelist
 * (`guard-manifest.ts`).
 *
 * Why centralize? A scattered invariant was the #1 bug family of the
 * campaign (stats/counters/⌘K/PATCH ignoring `deleted_at`). With a single
 * choke point + anti-regression guard, adding a raw read of `page` outside
 * the whitelist BREAKS the build (see `live/__tests__/live-access-guard.test.ts`).
 *
 * Schema NOTE (folio): `page` is the ONLY table with a trash bin,
 * self-referenced as a tree via `parent_page_id`. `page_version` and `block`
 * have NO `deleted_at` of their own; they belong to `page` with FK `ON DELETE cascade`
 * (physically dropped when the page is permanently deleted, see
 * `db/trash.rs`), and remain visible/editable as long as the page is alive.
 *
 * Parent-child cohort: `movePageToTrashMutation` (pages/mutations.ts) is a
 * WRITE-TIME CASCADE — moving a page to trash sets the SAME
 * `deleted_at` on its ENTIRE surviving subtree (recursive, not just direct
 * children). That is, in normal system state, if a page is
 * alive (`deleted_at IS NULL`) NONE of its ancestors is in trash
 * (`restorePageFromTrashMutation` reaffirms this: if the parent remains deleted upon
 * restore, the restored page is promoted to root instead of dangling orphaned
 * under a deleted parent). Hence `deleted_at IS NULL` alone suffices for
 * the normal case — the NOT EXISTS check below is DEFENSE IN DEPTH (one
 * level, not recursive) in case a future write path breaks that write
 * invariant, not the primary mechanism.
 */

/**
 * "Live page" predicates: page is not in trash AND (defense in
 * depth, one level) its direct parent is not either. Applied across ALL
 * live views/queries of `page`.
 */
export function livePageFilters() {
  return [isNull(page.deleted_at), parentPageNotTrashed(page.parent_page_id)];
}

/**
 * One-level NOT EXISTS: `parent_page_id` (if present) does not reference a
 * page with non-null `deleted_at`. Root page (`parent_page_id IS NULL`)
 * passes trivially. Alias `parent_page` avoids self-reference ambiguity
 * (outer table and subquery share the name `page`).
 */
function parentPageNotTrashed(parentPageId: unknown) {
  return sql`(${parentPageId} IS NULL OR NOT EXISTS (
    SELECT 1 FROM ${page} AS parent_page
    WHERE parent_page.id = ${parentPageId} AND parent_page.deleted_at IS NOT NULL
  ))`;
}
